import {
  Box,
  TextField,
  MenuItem,
  Button,
  Typography,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  InputAdornment,
  Paper,
  Grid,
  useMediaQuery,
  Tooltip,
} from "@mui/material";
import WorkOutlineIcon from "@mui/icons-material/WorkOutline";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import NotesIcon from "@mui/icons-material/Notes";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import { useState, useEffect } from "react";
import { API_BASE_URL } from "../config";

export default function MyTimePage() {
  const [project, setProject] = useState("");
  const [date, setDate] = useState("");
  const [startTime, setStartTime] = useState("");
  const [description, setDescription] = useState("");

  const [entries, setEntries] = useState([]);
  const [projects, setProjects] = useState([]);

  const [pauseModalOpen, setPauseModalOpen] = useState(false);
  const [selectedPauseId, setSelectedPauseId] = useState(null);
  const [pauseValue, setPauseValue] = useState("");

  const [validationError, setValidationError] = useState("");

  const fetchEntries = () => {
    const token = localStorage.getItem("token");

    fetch(`${API_BASE_URL}/api/user/time-logs`, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
      credentials: "include",
    })
      .then((res) => res.json())
      .then((data) => setEntries(data))
      .catch((err) => console.error(err));
  };

  useEffect(() => {
    const token = localStorage.getItem("token");

    fetch(`${API_BASE_URL}/api/user/my-projects`, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
      credentials: "include",
    })
      .then((res) => res.json())
      .then((data) => setProjects(data))
      .catch((err) => console.error(err));

    fetchEntries();
  }, []);

  const isMobile = useMediaQuery("(max-width:600px)");
  const cardSx = {
    p: { xs: 2, sm: 2.5 },
    mb: 3,
    borderRadius: 3,
    background: "#ffffff",
    boxShadow: "0 8px 30px rgba(0,0,0,0.06)",
    border: "1px solid #eef0f3",
  };

  const handlePause = (id) => {
    setSelectedPauseId(id);
    setPauseModalOpen(true);
  };

  const submitPause = () => {
    const token = localStorage.getItem("token");
    if (!pauseValue) return;

    fetch(`${API_BASE_URL}/api/user/time-logs/${selectedPauseId}/pause`, {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      credentials: "include",
      body: JSON.stringify({ pause_minutes: pauseValue }),
    })
      .then((res) => res.json())
      .then(() => {
        fetchEntries();
        setPauseModalOpen(false);
        setPauseValue("");
      })
      .catch((err) => console.error(err));
  };

  // Enforce numeric-only input and auto-insert ':' as HH:MM
  const handleStartTimeChange = (e) => {
    let v = (e.target.value || "").replace(/\D/g, ""); // keep digits only
    if (v.length > 4) v = v.slice(0, 4);
    if (v.length >= 3) {
      v = `${v.slice(0, 2)}:${v.slice(2)}`;
    }
    setStartTime(v);
  };

  const handleStartTimeKeyDown = (e) => {
    const allowed = [
      "Backspace",
      "Delete",
      "ArrowLeft",
      "ArrowRight",
      "Tab",
      "Home",
      "End",
    ];
    if (allowed.includes(e.key)) return;
    // Allow digits only
    if (!/^[0-9]$/.test(e.key)) {
      e.preventDefault();
    }
  };

  const normalizeStartTimeOnBlur = () => {
    const raw = (startTime || "").replace(/\D/g, "");
    if (raw.length === 0) return; // leave empty, validation will catch
    if (raw.length === 1) {
      // e.g. '9' -> '09:00'
      const hh = raw.padStart(2, "0");
      setStartTime(`${hh}:00`);
      return;
    }
    if (raw.length === 2) {
      // e.g. '09' -> '09:00'
      setStartTime(`${raw}:00`);
      return;
    }
    if (raw.length === 3) {
      // e.g. '930' -> '09:30'
      const hh = raw.slice(0, 2);
      const mm = raw.slice(2).padEnd(2, "0");
      setStartTime(`${hh}:${mm}`);
      return;
    }
    if (raw.length >= 4) {
      const hh = raw.slice(0, 2);
      const mm = raw.slice(2, 4);
      setStartTime(`${hh}:${mm}`);
    }
  };

  const handleSubmit = () => {
    if (!project || isNaN(Number(project)) || !date || !startTime) {
      setValidationError(
        "Fill in the required fields: Project, Date and Start."
      );

      return;
    }

    const timeRe = /^([01]\d|2[0-3]):[0-5]\d$/;
    if (!timeRe.test(startTime)) {
      setValidationError("Start time must be in HH:MM format (00-23:00-59).");
      return;
    }

    const activeEntry = entries.find((entry) => !entry.end_time);
    if (activeEntry) {
      setValidationError(
        "You must complete your ongoing task before starting a new one."
      );
      return;
    }

    const token = localStorage.getItem("token");

    const formattedStartTime =
      startTime.length === 5 ? `${startTime}:00` : startTime;

    fetch(`${API_BASE_URL}/api/user/time-logs`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      credentials: "include",
      body: JSON.stringify({
        project_id: Number(project),
        date: date,
        start_time: formattedStartTime,
        description: description || "",
        pause_minutes: null,
        end_time: null,
      }),
    })
      .then((res) => res.json())
      .then((data) => {
        setProject("");
        setDate("");
        setStartTime("");
        setDescription("");
        setValidationError("");
        fetchEntries();
      })
      .catch((err) => console.error(err));
  };

  return (
    <Box>
      <Box sx={{ mb: 2.5 }}>
        <Typography variant="h5" fontWeight={500} sx={{ letterSpacing: 0.2 }}>
          My Time
        </Typography>
        <Typography variant="body2" sx={{ color: "#6b7280" }}>
          Start a new time entry or manage your recent logs.
        </Typography>
      </Box>
      {validationError && (
        <Typography color="error" sx={{ mb: 2, fontSize: 13 }}>
          {validationError}
        </Typography>
      )}
      {/* Forma za unos */}
      <Paper elevation={0} sx={cardSx} className="my-time-form">
        <Grid
          container
          spacing={2}
          sx={{
            "& .MuiFormControl-root": { width: "100%" },
            "& .MuiInputBase-root": { width: "100%" },
            "& .MuiButton-root": { width: "100%" },
          }}
        >
          <Grid
            item
            xs={12}
            sm={6}
            md={3}
            sx={{
              minWidth: {
                xs: "100%",
                md: "100px",
              },
            }}
          >
            <TextField
              select
              fullWidth
              label="Project"
              value={project}
              onChange={(e) => setProject(e.target.value)}
              required
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <WorkOutlineIcon sx={{ fontSize: 20, color: "#9ca3af" }} />
                  </InputAdornment>
                ),
              }}
            >
              {Array.isArray(projects) &&
                projects.map((p) => (
                  <MenuItem key={p.id} value={Number(p.id)}>
                    {p.name}
                  </MenuItem>
                ))}
            </TextField>
          </Grid>

          <Grid
            item
            xs={12}
            sm={6}
            md={3}
            sx={{
              minWidth: {
                xs: "100%",
                md: "100px",
              },
            }}
          >
            <TextField
              fullWidth
              label="Date"
              type="date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
              required
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <CalendarMonthIcon
                      sx={{ fontSize: 20, color: "#9ca3af" }}
                    />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>

          <Grid
            item
            xs={12}
            sm={6}
            md={3}
            sx={{
              minWidth: {
                xs: "100%",
                md: "100px",
              },
            }}
          >
            <TextField
              fullWidth
              label="Start time"
              type="text"
              placeholder="HH:MM"
              value={startTime}
              onChange={handleStartTimeChange}
              onKeyDown={handleStartTimeKeyDown}
              onBlur={normalizeStartTimeOnBlur}
              InputLabelProps={{ shrink: true }}
              required
              inputProps={{
                inputMode: "numeric",
                pattern: "^([01]\\d|2[0-3]):[0-5]\\d$",
              }}
              sx={{ width: "100% !important" }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <AccessTimeIcon sx={{ fontSize: 20, color: "#9ca3af" }} />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>

          <Grid
            item
            xs={12}
            sm={12}
            md={3}
            sx={{
              minWidth: {
                xs: "100%",
                md: "100px",
              },
            }}
          >
            <TextField
              fullWidth
              label="Description"
              variant="outlined"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <NotesIcon sx={{ fontSize: 20, color: "#9ca3af" }} />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>

          <Grid
            item
            xs={12}
            sm={12}
            md={3}
            sx={{
              minWidth: {
                xs: "100%",
                md: "100px",
              },
            }}
          >
            <Button
              variant="contained"
              fullWidth
              sx={{
                width: "100% !important",
                height: 56,
                background: "var(--gradient-color)",
                borderRadius: 30,
              }}
              onClick={handleSubmit}
            >
              Save
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {/* Tabela */}

      <Box sx={{ overflowX: "auto", width: "100%" }}>
        <Table className="table">
          <TableHead>
            <TableRow>
              <TableCell>Project</TableCell>
              <TableCell>Data</TableCell>
              <TableCell>Start Time</TableCell>
              <TableCell>End Time</TableCell>
              <TableCell>Pause (min)</TableCell>
              <TableCell>Description</TableCell>
              <TableCell>Action</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {entries.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                  No my time...
                </TableCell>
              </TableRow>
            ) : (
              entries.map((entry, index) => (
                <TableRow key={index}>
                  <TableCell>{entry.project || "Nepoznat"}</TableCell>
                  <TableCell>{entry.created_at?.slice(0, 10)}</TableCell>
                  <TableCell>{entry.start_time}</TableCell>
                  <TableCell>
                    <Box
                      sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
                    >
                      <span>{entry.end_time || "-"}</span>
                      {entry.end_time &&
                        new Date(`1970-01-01T${entry.end_time}`) <=
                          new Date(`1970-01-01T${entry.start_time}`) && (
                          <Tooltip
                            title="End time must be after start time"
                            arrow
                          >
                            <ErrorOutlineIcon
                              sx={{
                                color: "#dc2626",
                                fontSize: 18,
                                cursor: "help",
                              }}
                            />
                          </Tooltip>
                        )}
                    </Box>
                  </TableCell>
                  <TableCell>{entry.pause_minutes ?? "-"}</TableCell>
                  <TableCell>{entry.description}</TableCell>
                  <TableCell>
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={() => handlePause(entry.id)}
                      disabled={!!entry.end_time}
                      sx={{ mr: 1, mb: 1, mt: 1 }}
                    >
                      Pause
                    </Button>
                    <Button
                      variant="contained"
                      size="small"
                      onClick={() => handleEnd(entry.id)}
                      disabled={!!entry.end_time}
                      sx={{ mb: 1, mt: 1 }}
                    >
                      Finish
                    </Button>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </Box>

      {pauseModalOpen && (
        <Box
          sx={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            bgcolor: "rgba(0,0,0,0.5)",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            zIndex: 2000,
          }}
        >
          <Box sx={{ bgcolor: "white", p: 4, borderRadius: 2, minWidth: 300 }}>
            <Typography variant="h6" gutterBottom>
              Enter the length of the break (min):
            </Typography>
            <TextField
              type="number"
              fullWidth
              value={pauseValue}
              onChange={(e) => setPauseValue(e.target.value)}
              sx={{ mb: 2 }}
            />
            <Button variant="contained" onClick={submitPause} sx={{ mr: 1 }}>
              Save
            </Button>
            <Button variant="outlined" onClick={() => setPauseModalOpen(false)}>
              Close
            </Button>
          </Box>
        </Box>
      )}
    </Box>
  );

  function handleEnd(id) {
    const token = localStorage.getItem("token");
    const now = new Date();

    const entry = entries.find((e) => e.id === id);
    if (!entry) return;

    const start = new Date(`${entry.date}T${entry.start_time}`);
    if (now <= start) {
      alert("End time must be after start time.");
      return;
    }

    // Format end time as 'YYYY-MM-DD HH:mm:ss'
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, "0");
    const day = String(now.getDate()).padStart(2, "0");
    const hours = String(now.getHours()).padStart(2, "0");
    const minutes = String(now.getMinutes()).padStart(2, "0");
    const seconds = String(now.getSeconds()).padStart(2, "0");

    const formattedDateTime = `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;

    fetch(`${API_BASE_URL}/api/user/time-logs/${id}/end`, {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      credentials: "include",
      body: JSON.stringify({ end_time: formattedDateTime }),
    })
      .then((res) => res.json())
      .then(() => fetchEntries())
      .catch((err) => console.error(err));
  }
}
